/**
 * Socket Logger Service
 * 
 * Logs all WebSocket events from SpeechStream.js for debugging purposes.
 * Captures both outgoing (emit) and incoming (received) events with their payloads.
 * 
 * Only captures when in debug mode (DEV/STAGE environment or showDebug URL param).
 */

class SocketLogger {
  constructor() {
    this.events = [];
    this.maxEvents = 3000;
    this.eventIndex = 0;
    this.isInitialized = false;
    this.debugModeActive = false;
  }
  
  /**
   * Checks if debug mode is active
   */
  isDebugModeActive() {
    const stack = process.env.FEATURE_FLAGS_STACK;
    if (stack === 'DEV' || stack === 'STAGE') {
      return true;
    }
    
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const showDebug = urlParams.get('showDebug');
      return showDebug === '1' || showDebug === 'true';
    }
    
    return false;
  }
  
  /**
   * Initializes the socket logger
   * Only activates if debug mode is enabled
   */
  init() {
    if (this.isInitialized) {
      return;
    }
    
    this.debugModeActive = this.isDebugModeActive();
    
    if (!this.debugModeActive) {
      return;
    }
    
    this.isInitialized = true;
    this.events = new Array(this.maxEvents);
    this.eventIndex = 0;
  }
  
  /**
   * Recursively sanitizes an object to replace binary data with size info
   */
  sanitizeBinaryData(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    
    // Handle Blob
    if (obj instanceof Blob) {
      return `[Blob: ${obj.size} bytes]`;
    }
    
    // Handle ArrayBuffer
    if (obj instanceof ArrayBuffer) {
      return `[ArrayBuffer: ${obj.byteLength} bytes]`;
    }
    
    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeBinaryData(item));
    }
    
    // Handle plain objects
    const sanitized = {};
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      
      if (value instanceof Blob) {
        sanitized[key] = `[Blob: ${value.size} bytes]`;
      } else if (value instanceof ArrayBuffer) {
        sanitized[key] = `[ArrayBuffer: ${value.byteLength} bytes]`;
      } else if (value && typeof value === 'object') {
        sanitized[key] = this.sanitizeBinaryData(value);
      } else {
        sanitized[key] = value;
      }
    }
    
    return sanitized;
  }
  
  /**
   * Sanitizes payload to remove large binary data and limit size
   */
  sanitizePayload(payload, maxLength = 2000) {
    if (!payload) return null;
    
    try {
      // Recursively sanitize binary data at any depth
      const sanitized = this.sanitizeBinaryData(payload);
      
      // Convert to string and truncate
      const jsonStr = JSON.stringify(sanitized, this.getCircularReplacer());
      if (jsonStr.length > maxLength) {
        return jsonStr.substring(0, maxLength) + '... [truncated]';
      }
      return jsonStr;
    } catch (e) {
      return '[Unable to serialize payload]';
    }
  }
  
  /**
   * Handles circular references in JSON.stringify
   */
  getCircularReplacer() {
    const seen = new WeakSet();
    return (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      // Handle special types
      if (value instanceof Blob) {
        return `[Blob: ${value.size} bytes]`;
      }
      if (value instanceof ArrayBuffer) {
        return `[ArrayBuffer: ${value.byteLength} bytes]`;
      }
      return value;
    };
  }
  
  /**
   * Logs an outgoing (emit) socket event
   * @param {string} eventName - Name of the event being emitted
   * @param {object} payload - Event payload
   * @param {object} services - Enabled services (if applicable)
   */
  logOutgoing(eventName, payload, services = null) {
    if (!this.debugModeActive) {
      return;
    }
    
    const entry = {
      timestamp: new Date().toISOString(),
      direction: 'OUT',
      event: eventName,
      payload: this.sanitizePayload(payload),
      services: services ? { ...services } : null,
    };
    
    // Use circular buffer
    const index = this.eventIndex % this.maxEvents;
    this.events[index] = entry;
    this.eventIndex++;
  }
  
  /**
   * Logs an incoming (received) socket event
   * @param {string} eventName - Name of the event received
   * @param {object} payload - Event payload
   * @param {string} service - Service that sent the event (if applicable)
   */
  logIncoming(eventName, payload, service = null) {
    if (!this.debugModeActive) {
      return;
    }
    
    const entry = {
      timestamp: new Date().toISOString(),
      direction: 'IN',
      event: eventName,
      payload: this.sanitizePayload(payload),
      service: service,
    };
    
    // Use circular buffer
    const index = this.eventIndex % this.maxEvents;
    this.events[index] = entry;
    this.eventIndex++;
  }
  
  /**
   * Logs a socket connection event
   * @param {string} eventType - Type of connection event (connect, disconnect, error, etc.)
   * @param {object} details - Event details
   */
  logConnection(eventType, details = null) {
    if (!this.debugModeActive) {
      return;
    }
    
    const entry = {
      timestamp: new Date().toISOString(),
      direction: 'CONN',
      event: eventType,
      payload: details ? this.sanitizePayload(details) : null,
      service: null,
    };
    
    // Use circular buffer
    const index = this.eventIndex % this.maxEvents;
    this.events[index] = entry;
    this.eventIndex++;
  }
  
  /**
   * Returns all logged events in chronological order
   */
  getEvents() {
    if (!this.debugModeActive) {
      return [];
    }
    
    const totalCaptured = Math.min(this.eventIndex, this.maxEvents);
    
    if (totalCaptured === 0) {
      return [];
    }
    
    // If we haven't filled the buffer yet, return events in order
    if (this.eventIndex <= this.maxEvents) {
      return this.events.slice(0, this.eventIndex).filter(Boolean);
    }
    
    // If buffer is full, reconstruct chronological order
    const startIndex = this.eventIndex % this.maxEvents;
    const orderedEvents = [];
    
    for (let i = 0; i < this.maxEvents; i++) {
      const index = (startIndex + i) % this.maxEvents;
      if (this.events[index]) {
        orderedEvents.push(this.events[index]);
      }
    }
    
    return orderedEvents;
  }
  
  /**
   * Gets statistics about logged events
   */
  getStats() {
    const events = this.getEvents();
    
    return {
      total: events.length,
      outgoing: events.filter(e => e.direction === 'OUT').length,
      incoming: events.filter(e => e.direction === 'IN').length,
      connection: events.filter(e => e.direction === 'CONN').length,
      maxEvents: this.maxEvents,
    };
  }
  
  /**
   * Clears all logged events
   */
  clearEvents() {
    this.events = new Array(this.maxEvents);
    this.eventIndex = 0;
  }
}

// Create singleton instance
const socketLogger = new SocketLogger();

export default socketLogger;

